const INSTALLATION_DATABASE_NAME = 'nettgrav-browser-installation';
const INSTALLATION_DATABASE_VERSION = 1;
const INSTALLATION_STORE_NAME = 'identity';
const INSTALLATION_RECORD_KEY = 'installation_id';
const INSTALLATION_FALLBACK_STORAGE_KEY = 'nettgrav.browser.installation_id.v1';
const INSTALLATION_LOCK_NAME = 'nettgrav-browser-installation-id';

let installationIdPromise = null;
let memoryInstallationId = null;

const toInstallationIdOrNull = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  if (!/^[A-Za-z0-9._:-]{8,255}$/.test(normalized)) return null;
  return normalized;
};

const generateInstallationId = () => {
  const cryptoRef =
    (typeof window !== 'undefined' && window.crypto) ||
    null;
  if (typeof cryptoRef?.randomUUID === 'function') {
    return cryptoRef.randomUUID();
  }

  const bytes = new Uint8Array(16);
  if (typeof cryptoRef?.getRandomValues === 'function') {
    cryptoRef.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const resolveFromIndexedDb = () =>
  new Promise((resolve, reject) => {
    const indexedDbRef = typeof window !== 'undefined' ? window.indexedDB : null;
    if (!indexedDbRef?.open) {
      reject(new Error('indexed_db_unavailable'));
      return;
    }

    const openRequest = indexedDbRef.open(
      INSTALLATION_DATABASE_NAME,
      INSTALLATION_DATABASE_VERSION,
    );
    openRequest.onupgradeneeded = () => {
      const database = openRequest.result;
      if (!database.objectStoreNames.contains(INSTALLATION_STORE_NAME)) {
        database.createObjectStore(INSTALLATION_STORE_NAME);
      }
    };
    openRequest.onerror = () => reject(openRequest.error || new Error('indexed_db_open_failed'));
    openRequest.onsuccess = () => {
      const database = openRequest.result;
      const transaction = database.transaction(INSTALLATION_STORE_NAME, 'readwrite');
      const store = transaction.objectStore(INSTALLATION_STORE_NAME);
      let resolvedInstallationId = null;

      transaction.oncomplete = () => {
        database.close();
        resolve(resolvedInstallationId);
      };
      transaction.onerror = () => {
        const error = transaction.error || new Error('indexed_db_transaction_failed');
        database.close();
        reject(error);
      };
      transaction.onabort = transaction.onerror;

      const readRequest = store.get(INSTALLATION_RECORD_KEY);
      readRequest.onerror = () => transaction.abort();
      readRequest.onsuccess = () => {
        const existing = toInstallationIdOrNull(readRequest.result);
        if (existing) {
          resolvedInstallationId = existing;
          return;
        }

        resolvedInstallationId = generateInstallationId();
        store.put(resolvedInstallationId, INSTALLATION_RECORD_KEY);
      };
    };
  });

const resolveFromFallbackStorage = async () => {
  const resolveUnderLock = () => {
    try {
      const storage = typeof window !== 'undefined' ? window.localStorage : null;
      const existing = toInstallationIdOrNull(
        storage?.getItem(INSTALLATION_FALLBACK_STORAGE_KEY),
      );
      if (existing) return existing;

      const generated = generateInstallationId();
      storage?.setItem(INSTALLATION_FALLBACK_STORAGE_KEY, generated);
      return (
        toInstallationIdOrNull(storage?.getItem(INSTALLATION_FALLBACK_STORAGE_KEY)) || generated
      );
    } catch {
      if (!memoryInstallationId) {
        memoryInstallationId = generateInstallationId();
      }
      return memoryInstallationId;
    }
  };

  const lockManager =
    typeof navigator !== 'undefined' && typeof navigator?.locks?.request === 'function'
      ? navigator.locks
      : null;
  return lockManager
    ? lockManager.request(INSTALLATION_LOCK_NAME, { mode: 'exclusive' }, resolveUnderLock)
    : resolveUnderLock();
};

// This is the one browser-profile identity source used by authentication and
// every MLS request. IndexedDB read/write transactions serialize creation
// across tabs, so simultaneous windows cannot mint competing device IDs.
export const getBrowserInstallationId = async () => {
  if (!installationIdPromise) {
    installationIdPromise = resolveFromIndexedDb()
      .catch(() => resolveFromFallbackStorage())
      .then((value) => {
        const normalized = toInstallationIdOrNull(value);
        if (!normalized) throw new Error('browser_installation_id_unavailable');
        return normalized;
      })
      .catch((error) => {
        installationIdPromise = null;
        throw error;
      });
  }
  return installationIdPromise;
};

export const __resetBrowserInstallationIdForTests = () => {
  installationIdPromise = null;
  memoryInstallationId = null;
};

export {
  INSTALLATION_DATABASE_NAME,
  INSTALLATION_FALLBACK_STORAGE_KEY,
  INSTALLATION_RECORD_KEY,
  INSTALLATION_STORE_NAME,
};
